import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { AuthLayout, SubmitButton } from "@/components/auth/AuthLayout";
import { passwordOk } from "@/components/auth/PasswordRules";
import { NewPasswordFields } from "@/pages/ChangePassword";
import { api, errMsg } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const member = params.get("type") === "member";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!passwordOk(pw)) return toast.error("Your new password does not meet the rules");
    if (pw !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
    } catch (err) {
      toast.error(errMsg(err));
    }
    setBusy(false);
  };

  return (
    <AuthLayout>
      {done ? (
        <div data-testid="reset-done">
          <CheckCircle2 className="w-12 h-12 text-emerald-600" />
          <h2 className="font-heading text-3xl font-extrabold tracking-tight mt-4">Password updated</h2>
          <p className="text-sm text-gray-500 mt-1">You can now sign in with your new password.</p>
          <Link to="/login" className="mt-6 h-11 px-5 inline-flex items-center rounded-md bg-[#E60000] hover:bg-[#CC0000] text-white font-semibold transition-colors" data-testid="reset-go-login">Go to sign in</Link>
        </div>
      ) : !token ? (
        <p className="text-sm text-gray-600" data-testid="reset-missing-token">This reset link is incomplete. Request a new one from the sign-in page.</p>
      ) : (
        <form onSubmit={submit} data-testid="reset-password-form">
          <h2 className="font-heading text-3xl font-extrabold tracking-tight">Choose a new password</h2>
          <p className="text-sm text-gray-500 mt-1">This link can be used once and expires 1 hour after it was sent.</p>
          <div className="mt-8 space-y-5">
            <NewPasswordFields pw={pw} setPw={setPw} confirm={confirm} setConfirm={setConfirm} />
            <SubmitButton busy={busy} testId="reset-password-submit">{busy ? "Saving…" : "Save new password"}</SubmitButton>
          </div>
        </form>
      )}
    </AuthLayout>
  );
}
